import { useCallback } from 'react'
import { toastManager } from '@systemeio/components'
import { useTranslation } from 'next-i18next/pages'
import type { KeyedMutator } from 'swr'
import useSWR from 'swr'
import { getPaginationAndFilterQueryString } from 'shared/api/utils/get-pagination-and-filter-query-string'
import { useApiWrapperWithErrorValidation } from 'shared/hooks/use-api-wrapper-with-error-validation'
import { RequestMethodsEnum } from 'shared/hooks/use-api-wrapper-with-error-validation/types'
import useUser from 'shared/hooks/use-user'
import type { SwrPaginationInterface } from 'shared/types/swr-pagination-interface'
import { NOTIFICATION_API_URL } from 'modules/profile/notifications/api/notification-center'
import type {
  NotificationsApiInterface,
  NotificationsFilterInterface,
} from 'modules/profile/notifications/types/notification-types'

interface UseNotificationsInterface extends SwrPaginationInterface {
  shouldFetch?: boolean
  filter: NotificationsFilterInterface
}

export interface HasUnreadResponse {
  hasUnread: boolean
}

export const useNotifications = ({
  limit,
  shouldFetch = true,
  pagination,
  filter,
}: UseNotificationsInterface): {
  notificationsMeta: HasUnreadResponse | undefined
  mutateNotificationsMeta: KeyedMutator<HasUnreadResponse>
  notifications: NotificationsApiInterface | undefined
  isFetching: boolean
  mutate: KeyedMutator<NotificationsApiInterface>
} => {
  const { t } = useTranslation()

  const notFoundResponseHandler = useCallback(() => {
    toastManager.add({
      type: 'error',
      title: t('profile.notifications.error_loading_notifications'),
    })
  }, [t])

  const { user } = useUser()
  const { fetcher } = useApiWrapperWithErrorValidation<
    RequestMethodsEnum.get,
    NotificationsApiInterface
  >({
    method: RequestMethodsEnum.get,
    notFoundResponseHandler,
  })

  const { fetcher: unreadFetcher } = useApiWrapperWithErrorValidation<
    RequestMethodsEnum.get,
    HasUnreadResponse
  >({
    method: RequestMethodsEnum.get,
    notFoundResponseHandler,
  })

  const {
    data: notifications,
    mutate,
    isLoading,
  } = useSWR<NotificationsApiInterface>(() => {
    if (user && shouldFetch) {
      return `${NOTIFICATION_API_URL}/notifications?${getPaginationAndFilterQueryString({
        limit,
        pagination,
        filter: { ...filter },
      })}`
    }
    return null
  }, fetcher)

  const {
    data: notificationsMeta,
    mutate: mutateNotificationsMeta,
    isLoading: isLoadingNotificationsMeta,
  } = useSWR<HasUnreadResponse>(() => {
    if (user) {
      return `${NOTIFICATION_API_URL}/notifications/has-unread`
    }
    return null
  }, unreadFetcher)

  return {
    notificationsMeta,
    mutateNotificationsMeta,
    notifications,
    isFetching: isLoading || isLoadingNotificationsMeta,
    mutate,
  }
}
